const MAX_SERIALIZED_LENGTH = 8000;

const REDACT_KEYS = new Set([
  "password",
  "token",
  "access_token",
  "refresh_token",
  "authorization",
  "secret",
]);

function truncateValue(value: unknown): unknown {
  try {
    const serialized = JSON.stringify(value);
    if (serialized.length <= MAX_SERIALIZED_LENGTH) {
      return value;
    }

    return `${serialized.slice(0, MAX_SERIALIZED_LENGTH)}… [truncated]`;
  } catch {
    return "[unserializable]";
  }
}

/** Redact secrets and cap size for debug capture exports (zip, manifest). */
export function sanitizeCaptureValue(value: unknown): unknown {
  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === "string") {
    try {
      return sanitizeCaptureValue(JSON.parse(value) as unknown);
    } catch {
      return value.length > MAX_SERIALIZED_LENGTH
        ? `${value.slice(0, MAX_SERIALIZED_LENGTH)}… [truncated]`
        : value;
    }
  }

  if (Array.isArray(value)) {
    return value.map((entry) => sanitizeCaptureValue(entry));
  }

  if (typeof value === "object") {
    if (value instanceof FormData) {
      return "[FormData]";
    }

    if (value instanceof Blob) {
      return `[Blob ${value.type || "unknown"}]`;
    }

    const record = value as Record<string, unknown>;
    const sanitized: Record<string, unknown> = {};

    for (const [key, entry] of Object.entries(record)) {
      if (REDACT_KEYS.has(key.toLowerCase())) {
        sanitized[key] = "[redacted]";
        continue;
      }

      sanitized[key] = sanitizeCaptureValue(entry);
    }

    return truncateValue(sanitized);
  }

  return value;
}

export function serializeCaptureRecord(record: unknown): string | undefined {
  const sanitized = sanitizeCaptureValue(record);
  if (!sanitized || typeof sanitized !== "object") {
    return undefined;
  }

  return JSON.stringify(sanitized, null, 2);
}
